import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Film, Gift, LayoutGrid } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ExtractorApp } from './extractor-app'
import { GifCreatorPage } from './gif-creator-page'
import { UnifiedLibraryPage } from './unified-library-page'

type WorkbenchView = 'studio' | 'gif' | 'library'

function resolveView(value: string | null): WorkbenchView {
  if (value === 'gif' || value === 'library') return value
  return 'studio'
}

export function WorkbenchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeView = useMemo(() => resolveView(searchParams.get('view')), [searchParams])

  const handleViewChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('view', resolveView(value))
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container mx-auto flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">FrameX workspace</h1>
            <p className="text-sm text-muted-foreground">Capture frames, build GIFs, and manage local media.</p>
          </div>
          <Tabs value={activeView} onValueChange={handleViewChange} className="w-full lg:w-auto">
            <TabsList className="grid h-10 w-full grid-cols-3 lg:w-auto">
              <TabsTrigger value="studio" className="gap-2">
                <Film className="h-4 w-4" />
                Video
              </TabsTrigger>
              <TabsTrigger value="gif" className="gap-2">
                <Gift className="h-4 w-4" />
                GIF
              </TabsTrigger>
              <TabsTrigger value="library" className="gap-2">
                <LayoutGrid className="h-4 w-4" />
                Library
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <Tabs value={activeView} onValueChange={handleViewChange} className="min-h-0">
        <TabsContent value="studio" className="mt-0">
          <ExtractorApp embedded />
        </TabsContent>
        <TabsContent value="gif" className="mt-0">
          <GifCreatorPage embedded />
        </TabsContent>
        <TabsContent value="library" className="mt-0 p-4 md:p-6">
          <UnifiedLibraryPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  )
}